import React, {useState, useEffect, useRef} from "react";
import socket from "../utils/clientSocket";
import {useUser} from "../UserContext";
import UserLink from "./UserLink";
import Avatar from "./ui/Avatar";
import Icon from "./ui/Icon";
import {RookFace} from "./rook/RookAvatar";

const MAX_LENGTH = 300;

// Rook writes plain text, but may use **bold**
const renderText = (text) => String(text || "").split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") && part.length > 4 ? <strong key={i}>{part.slice(2, -2)}</strong> : part);

const MUTE_KEY = "chesscorner.muteRook";
const readMuted = () => {
    try {
        return localStorage.getItem(MUTE_KEY) === "1";
    } catch (e) {
        return false;
    }
};

/**
 * A chat room. `withRook` (the front page chat) shows Rook's messages with his face and a "Rook is typing" line,
 * and lets you mute him, which hides his messages. Writing @Rook unmutes him, since you clearly want his answer.
 */
const Chat = ({chatType, chatId, placeholder = "Write a message…", emptyText = "No messages yet. Say hello!", withRook = false}) => {
    const [messages, setMessages] = useState([]);
    const [rookMuted, setRookMutedState] = useState(() => withRook && readMuted());
    const setRookMuted = (value) => {
        setRookMutedState(value);
        try {
            localStorage.setItem(MUTE_KEY, value ? "1" : "0");
        } catch (e) {
            // ignore
        }
    };
    const [rookTyping, setRookTyping] = useState(false);
    const [chatInput, setChatInput] = useState("");
    const listRef = useRef(null)
    const user = useUser()

    useEffect(() => {
        setMessages([]);
        const handleMessage = (msg) => {
            // A socket belongs to several chats (global, club, game...) - only show the ones for this chat
            if (msg.chatId !== chatId) return;
            setMessages(prevState => [...prevState, msg]);
        };
        const handleHistory = (chatHistory) => {
            if (!Array.isArray(chatHistory)) return;
            if (chatHistory.length > 0 && chatHistory[0].chatId !== chatId) return;
            setMessages(chatHistory);
        };

        const handleTyping = ({chatId: typingChat, typing}) => {
            if (typingChat === chatId) setRookTyping(!!typing);
        };

        socket.on('chatMessage', handleMessage);
        socket.on("chatHistory", handleHistory);
        socket.on("rookTyping", handleTyping);
        socket.emit("getChatHistory", chatId);

        return () => {
            socket.off('chatMessage', handleMessage);
            socket.off("chatHistory", handleHistory);
            socket.off("rookTyping", handleTyping);
        };
    }, [chatId]);

    useEffect(() => {
        const list = listRef.current;
        if (list) list.scrollTop = list.scrollHeight;
    }, [messages, rookTyping, rookMuted])

    // Rook stops "typing" if his message arrived
    useEffect(() => {
        if (messages.length && messages[messages.length - 1].user?.isRook) setRookTyping(false);
    }, [messages]);

    const shown = withRook && rookMuted ? messages.filter((m) => !m.user?.isRook) : messages;

    const sendMessage = (event) => {
        event?.preventDefault();
        const text = chatInput.trim();
        if (!text) return;

        const newMsg = {text, chatType, chatId, timeSent: Date.now()};
        // Asking Rook directly means you want to hear from him again
        const asksRook = withRook && /@rook\b/i.test(text);
        if (asksRook && rookMuted) setRookMuted(false);
        socket.emit('chatMessage', newMsg);

        // The server looks up who we are, so we only attach our own user object locally
        setMessages(prevState => [...prevState, {...newMsg, user}]);
        setChatInput("");
    };

    return (
        <div className="chat">
            {withRook && (
                <div className="chat__rook-note">
                    <RookFace size={18}/>
                    <span className="chat__rook-text">{rookMuted ? "Rook is muted. Write @Rook to ask him anyway." : "Rook drops by now and then. Write @Rook to ask him something."}</span>
                    <button type="button" className="link-btn" onClick={() => setRookMuted(!rookMuted)}>
                        {rookMuted ? "Unmute" : "Mute Rook"}
                    </button>
                </div>
            )}
            <div className="chat__messages" ref={listRef}>
                {shown.length === 0 && (
                    <div className="empty-state" style={{margin: "auto"}}>
                        <Icon name="chat"/>
                        <span>{emptyText}</span>
                    </div>
                )}
                {shown.map((msg, idx) => {
                    if (msg.user?.isRook) {
                        return (
                            <div key={idx} className="chat-msg chat-msg--rook">
                                <RookFace size={28}/>
                                <div className="chat-msg__body">
                                    <div className="chat-msg__meta">
                                        <strong>Rook</strong>
                                        <span className="chip chip--leaf chat-msg__ai">AI</span>
                                        <span className="chat-msg__time">
                                            {new Date(msg.timeSent).toLocaleTimeString('en-GB', {hour: "2-digit", minute: "2-digit"})}
                                        </span>
                                    </div>
                                    <div className="chat-msg__text">{renderText(msg.text)}</div>
                                </div>
                            </div>
                        );
                    }
                    const isSelf = msg.user?.username && msg.user.username === user?.username;
                    const guest = msg.user && msg.user.isActualUser === false && !msg.user.isLoggedIn;
                    return (
                        <div key={idx} className={`chat-msg ${isSelf ? "chat-msg--self" : ""}`}>
                            <Avatar name={msg.user?.username} size={28} guest={guest}/>
                            <div className="chat-msg__body">
                                <div className="chat-msg__meta">
                                    {msg.user ? <UserLink user={msg.user}/> : <strong>Someone</strong>}
                                    <span className="chat-msg__time">
                                        {new Date(msg.timeSent).toLocaleTimeString('en-GB', {hour: "2-digit", minute: "2-digit"})}
                                    </span>
                                </div>
                                <div className="chat-msg__text">{msg.text}</div>
                            </div>
                        </div>
                    );
                })}
                {withRook && rookTyping && !rookMuted && (
                    <div className="chat-msg chat-msg--rook chat-msg--typing">
                        <RookFace size={28}/>
                        <div className="chat-msg__body">
                            <div className="chat-msg__meta"><strong>Rook</strong> <span className="chat-msg__time">is typing</span></div>
                            <span className="rook-typing" aria-label="Rook is typing"><i/><i/><i/></span>
                        </div>
                    </div>
                )}
            </div>
            <form className="chat__composer" onSubmit={sendMessage}>
                <input
                    type="text"
                    className="input"
                    value={chatInput}
                    maxLength={MAX_LENGTH}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder={placeholder}
                    aria-label="Chat message"
                />
                <button type="submit" className="icon-btn" disabled={!chatInput.trim()} aria-label="Send message">
                    <Icon name="send"/>
                </button>
            </form>
        </div>
    );
};

export default Chat;
